import {
  AccessibleOutlined,
  ApartmentOutlined,
  EmailOutlined,
  HomeOutlined,
  Phone,
  SecurityOutlined,
  WcOutlined,
} from '@mui/icons-material';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { Lock } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import HospitalDialog from '../../common/dialog/commonDialogBox';
import { APP_LINK } from '../../common/menu/menuItems/menuList';
import ReusableFormFields from '../../common/textfield/ReusableTextfield';
import { styles } from '../../commonStyle/formStyles';
import { fetchBlocks } from '../../redux/block/blockAction';
import { fetchDepartments } from '../../redux/department/departmentAction';
import { fetchFloors } from '../../redux/floor/floorAction';
import { fetchAllRoles } from '../../redux/role/roleAction';
import {
  fetchStaffMember,
  resetCurrentStaff,
  updateStaff,
} from '../../redux/staff/staffAction';
import { fetchWards } from '../../redux/ward/wardAction';

function EditStaff() {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    setError,
    clearErrors,
    control,
  } = useForm();

  const { id } = useParams();
  const dispatch = useDispatch();
  const [currentPage, setcurrentPage] = useState(1);
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [specificErrors, setSpecificErrors] = useState({
    email: null,
    phone: null,
  });

  const watchBlock = watch('block_id');
  const watchWard = watch('ward_id');
  const watchFloor = watch('floor_id');

  const staffStates = useSelector((state) => state?.staffs?.fetchStaff || {});
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const roleState = useSelector((state) => state?.role?.fetchAllRoles || {});
  const staffState = useSelector((state) => state?.staffs?.updateStaff);

  useEffect(() => {
    dispatch(fetchAllRoles());
    dispatch(fetchDepartments());
    dispatch(fetchBlocks());
  }, [dispatch]);

  const getBlockOptions = () => {
    return (
      blockList?.Blocks?.data
        ?.filter((block) => block.block_status !== 2)
        .map((block) => ({
          value: block.id,
          label: block.block_name,
        })) || []
    );
  };

  const getFloorOptions = () => {
    return (
      floorList?.floors?.data
        ?.filter((floor) => floor.floor_status !== 0)
        .map((floor) => ({
          value: floor.id,
          label: floor.floor_name,
        })) || []
    );
  };

  const getWardOptions = () => {
    if (wardList?.wards?.data) {
      return wardList.wards.data
        .filter((ward) => ward.ward_status !== 0)
        .map((ward) => ({
          value: ward.id,
          label: ward.ward_name,
        }));
    }
    return [];
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchStaffMember(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const staffData = staffStates?.data?.data;
    if (staffData) {
      setValue('staff_id', staffData.staff_id || '');
      setValue('staff_name', staffData.staff_name || '');
      setValue('email', staffData.email || '');
      setValue('phone', staffData.phone || '');
      setValue('block_id', staffData.block_id || '');
      setValue('gender_type', staffData.gender_type || '');
      setValue('ward_id', staffData.ward_id || '');
      setValue('floor_id', staffData.floor_id || '');
      setValue('specialization_id', staffData.specialization_id || '');
      setValue('address', staffData.address || '');
      setValue('role_id', staffData.role_id || '');
      setValue('status', staffData?.User?.status || '');

      if (staffData.block_id) localStorage.setItem('block', staffData.block_id);
      if (staffData.ward_id) localStorage.setItem('ward', staffData.ward_id);
      if (staffData.floor_id) localStorage.setItem('floor', staffData.floor_id);
    }
  }, [staffStates, setValue]);

  useEffect(() => {
    if (watchBlock) {
      localStorage.setItem('block', watchBlock);
      dispatch(fetchFloors(watchBlock, currentPage));
    }
  }, [watchBlock, dispatch, currentPage]);

  useEffect(() => {
    if (watchBlock && watchFloor) {
      localStorage.setItem('floor', watchFloor);
      dispatch(fetchWards(watchBlock, watchFloor, currentPage));
    }
  }, [watchBlock, watchFloor, dispatch, currentPage]);

  // Modified error handling
  useEffect(() => {
    if (staffState) {
      // Check for email uniqueness error
      if (
        staffState.error &&
        staffState.errorMessage === 'email must be unique'
      ) {
        setError('email', {
          type: 'manual',
          message: 'Email already exists',
        });
      }
      // Check for phone uniqueness error
      else if (
        staffState.error &&
        staffState.errorMessage === 'phone must be unique'
      ) {
        setError('phone', {
          type: 'manual',
          message: 'Phone number already exists',
        });
      }
      // Check for email associated with different phone number
      else if (
        staffState.error &&
        staffState.errorMessage ===
          'This email id already added in diffrent mobile number'
      ) {
        setError('email', {
          type: 'manual',
          message:
            'This email is already associated with a different phone number',
        });
      }
      // Handle successful staff update
      else if (staffState.success) {
        setIsSuccess(true);
        setOpenDialog(true);
        setDialogMessage({
          title: 'Success',
          message: 'Staff updated successfully.',
          type: 'success',
        });
        dispatch(resetCurrentStaff());
      }
      // Handle general error scenario
      else if (!staffState.success && staffState.error) {
        setIsSuccess(false);
        setOpenDialog(true);
        setDialogMessage(staffState.errorMessage || 'An error occurred');
      }
    }
  }, [staffState, setError, dispatch]);
  useEffect(() => {
    return () => {
      dispatch(resetCurrentStaff());
    };
  }, [dispatch]);
  // Modified input fields with error text handling
  const inputFields = [
    {
      name: 'staff_id',
      label: 'Staff Id',
      placeholder: 'Enter Staff Id',
      tabIndex: 1,
      type: 'text',
      rules: { required: 'Staff Id is required' },
      startIcon: <SecurityOutlined />,
    },
    {
      name: 'staff_name',
      label: 'Staff Name',
      placeholder: 'Enter name',
      tabIndex: 2,
      type: 'text',
      rules: { required: 'Name is required' },
      startIcon: <SecurityOutlined />,
    },
    {
      name: 'email',
      label: 'Email',
      placeholder: 'Enter email',
      type: 'email',
      tabIndex: 3,
      rules: { required: 'Email is required' },
      startIcon: <EmailOutlined />,
      errorText: errors.email?.message,
    },
    {
      name: 'phone',
      label: 'Phone Number',
      placeholder: 'Enter phone number',
      type: 'tel',
      tabIndex: 4,
      maxLength: 10,
      showCharCount: false,
      rules: {
        required: 'Phone number is required',
        pattern: {
          value: /^(6|7|8|9)\d{9}$/,
          message: 'Invalid phone number',
        },
        maxLength: {
          value: 10,
          message: 'Phone number should be 10 digits',
        },
      },
      startIcon: <Phone />,
      errorText: errors.phone?.message,
    },
    {
      name: 'block_id',
      label: 'Block',
      placeholder: 'Enter block',
      type: 'select',
      tabIndex: 5,
      options: getBlockOptions(),
      rules: { required: 'Block is required' },
      startIcon: <ApartmentOutlined />,
    },
    {
      name: 'floor_id',
      label: 'Floor',
      placeholder: 'Enter Floor',
      type: 'select',
      tabIndex: 6,
      options: getFloorOptions(),
      rules: { required: 'Floor is required' },
      startIcon: <ApartmentOutlined />,
      disabled: !watchBlock,
    },
    {
      name: 'ward_id',
      label: 'Ward Name',
      placeholder: 'Select ward name',
      type: 'select',
      tabIndex: 7,
      options: getWardOptions(),
      rules: { required: 'Ward name is required' },
      startIcon: <HomeOutlined />,
      disabled: !watchBlock || !watchFloor,
    },
    {
      name: 'address',
      label: 'Address',
      placeholder: 'Enter address',
      type: 'text',
      tabIndex: 8,
      rules: { required: 'Address is required' },
      startIcon: <HomeOutlined />,
    },
    {
      name: 'gender_type',
      label: 'Gender',
      placeholder: 'Select gender',
      type: 'select',
      tabIndex: 9,
      options: [
        { value: 'M', label: 'Male' },
        { value: 'F', label: 'Female' },
      ],
      rules: { required: 'Gender is required' },
      startIcon: <WcOutlined />,
    },
    {
      name: 'role_id',
      label: 'Role',
      placeholder: 'Select Position',
      type: 'select',
      tabIndex: 10,
      options:
        roleState?.Roles?.data?.map((role) => ({
          value: role.id,
          label: role.name,
        })) || [],
      rules: { required: 'Position is required' },
      startIcon: <WcOutlined />,
    },
    {
      name: 'password',
      label: 'Password',
      placeholder: 'Enter password (optional)',
      type: 'password',
      tabIndex: 11,
      maxLength: 10,
      showCharCount: false,
      rules: {
        minLength: {
          value: 10,
          message: 'Password must be exactly 10 characters',
        },
        maxLength: {
          value: 10,
          message: 'Password must be exactly 10 characters',
        },
      },
      startIcon: <Lock />,
    },
    {
      name: 'status',
      label: 'Status',
      placeholder: 'Select status',
      type: 'select',
      tabIndex: 12,
      options: [
        { value: 1, label: 'Active' },
        { value: 2, label: 'Inactive' },
        { value: 3, label: 'InProgress' },
      ],
      rules: { required: 'Status is required' },
      startIcon: <AccessibleOutlined />,
    },
  ];

  const onSubmit = (data) => {
    // Clear previous errors
    clearErrors(['email', 'phone']);

    const formData = {
      address: data.address,
      email: data.email,
      phone: data.phone,
      staff_id: data.staff_id,
      ward_id: data.ward_id,
      floor_id: data.floor_id,
      staff_name: data.staff_name,
      block_id: data.block_id,
      gender_type: data.gender_type,
      role_id: data.role_id,
      status: data.status,
      pincode: '',
      // Only include password if it was entered (not empty)
      ...(data.password && { password: data.password }),
    };

    dispatch(updateStaff(id, formData));
  };

  const handleCancel = () => {
    reset();
    clearErrors();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    if (isSuccess) {
      navigate(APP_LINK.LISTALLSTAFF);
    }
  };

  const navigateToStaffList = () => {
    navigate(APP_LINK.LISTALLSTAFF);
  };

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper} elevation={2}>
        <Grid container spacing={2} mb={5}>
          <Grid item xs={12} sm={6} md={8} lg={10} xl={10}>
            <Typography variant="h3" sx={styles.title}>
              Edit Staff
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2} xl={2} sx={styles.backButton}>
            <CustomButton
              variant="contained"
              color="primary"
              onClick={navigateToStaffList}
            >
              Back
            </CustomButton>
          </Grid>
        </Grid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ReusableFormFields
                inputFields={inputFields}
                register={register}
                errors={errors}
                setValue={setValue}
                control={control}
                gridProps={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
              />
            </Grid>
          </Grid>

          <Box sx={styles.buttonsContainer}>
            <CustomButton
              type="button"
              text="Cancel"
              variant="contained"
              disableElevation
              size="small"
              onClick={handleCancel}
              tabIndex={13}
            />
            <CustomButton
              type="submit"
              text="Submit"
              variant="contained"
              disableElevation
              size="small"
              disabled={staffState?.inProgress}
              loading={staffState?.inProgress}
              tabIndex={14}
            />
          </Box>
        </form>
      </Paper>
      <HospitalDialog
        open={openDialog}
        onClose={handleCloseDialog}
        isSuccess={isSuccess}
        dialogMessage={dialogMessage.message}
        title={isSuccess ? 'Staff Update' : 'Error'}
        navigateTo={isSuccess ? navigateToStaffList : null}
      />
    </Box>
  );
}

export default EditStaff;
